/**
 * Blog Filtering and Sorting
 * Uses Shopify's native tags for categorization
 */
class BlogFilters {
  constructor() {
    this.list = document.querySelector('.main-blog__list');
    
    if (!this.list) return; // Exit if no list found
    
    this.articles = Array.from(document.querySelectorAll('.main-blog__item'));
    this.filterSelects = document.querySelectorAll('.main-blog__filter-select');
    this.articlesPerPage = parseInt(this.list.dataset.articlesPerPage || '6');
    this.filtersEnabled = this.list.dataset.filtersEnabled === 'true';
    this.sortEnabled = this.list.dataset.sortEnabled === 'true';
    
    this.filters = {
      tag: '',
      sort: 'newest',
      page: 1
    };
    
    // Check if we should initialize filtering/sorting
    const hasFilterControls = this.filterSelects.length > 0;
    const needsFiltering = (this.filtersEnabled || this.sortEnabled) && hasFilterControls;
    
    if (this.articles.length === 0) return; // No articles to manage
    
    if (needsFiltering) {
      // Full filtering and sorting enabled
      this.init();
    } else {
      // No filters, just handle initial pagination
      this.showArticles(this.articles.slice(0, this.articlesPerPage));
      this.hideServerPagination();
    }
  }

  init() {
    // Attach event listeners
    this.filterSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        const filterType = e.target.dataset.filter;
        
        if (filterType === 'category') {
          this.filters.tag = e.target.value;
          this.filters.page = 1;
        } else if (filterType === 'sort') {
          this.filters.sort = e.target.value;
          this.filters.page = 1;
        }
        
        this.update();
      });
    });
    
    // Initial render
    this.update();
  }

  update() {
    // Get filtered and sorted articles (create new array to avoid mutations)
    let filtered = this.filterByTag();
    let sorted = this.sortByDate(filtered);
    
    // Calculate pagination
    const totalPages = Math.ceil(sorted.length / this.articlesPerPage);
    const startIndex = (this.filters.page - 1) * this.articlesPerPage;
    const endIndex = startIndex + this.articlesPerPage;
    const visible = sorted.slice(startIndex, endIndex);
    
    // Update display
    this.showArticles(visible);
    this.updatePagination(totalPages);
    this.hideServerPagination();
  }

  filterByTag() {
    // Always return a new array to avoid mutations
    if (!this.filters.tag) {
      return [...this.articles];
    }
    
    return this.articles.filter(article => {
      const tags = (article.dataset.tags || '').split(',').map(t => t.trim());
      return tags.includes(this.filters.tag);
    });
  }

  sortByDate(articles) {
    // Create a new sorted array (don't mutate the input)
    return [...articles].sort((a, b) => {
      const dateA = parseInt(a.dataset.date) || 0;
      const dateB = parseInt(b.dataset.date) || 0;
      return this.filters.sort === 'oldest' ? dateA - dateB : dateB - dateA;
    });
  }

  showArticles(visible) {
    // Hide all articles first
    this.articles.forEach(article => {
      article.style.display = 'none';
      article.style.order = '';
    });
    
    // Reorder and show visible articles in the correct order
    visible.forEach((article, index) => {
      article.style.display = '';
      article.style.order = index; // Use CSS order to control visual order
    });
  }

  updatePagination(totalPages) {
    if (totalPages <= 1) {
      this.removePagination();
      return;
    }
    
    let container = document.querySelector('.main-blog__client-pagination');
    const wrapper = document.querySelector('.main-blog__list-wrapper');
    
    if (!container) {
      container = document.createElement('div');
      container.className = 'main-blog__client-pagination';
      wrapper?.parentNode?.insertBefore(container, wrapper.nextSibling);
    }
    
    container.style.display = '';
    container.innerHTML = this.buildPaginationHTML(totalPages);
    
    // Add click handlers
    container.querySelectorAll('a[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.filters.page = parseInt(link.dataset.page);
        this.update();
        wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  buildPaginationHTML(totalPages) {
    const { page } = this.filters;
    const icon = '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';
    
    const prevBtn = page > 1 
      ? `<li><a href="#" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-page="${page - 1}" aria-label="Previous"><span class="svg-wrapper">${icon}</span></a></li>`
      : '';
    
    const nextBtn = page < totalPages
      ? `<li><a href="#" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-page="${page + 1}" aria-label="Next"><span class="svg-wrapper">${icon}</span></a></li>`
      : '';
    
    return `
      <div class="pagination-wrapper">
        <nav class="pagination" role="navigation" aria-label="Pagination">
          <ul class="pagination__list list-unstyled" role="list">
            ${prevBtn}
            ${this.buildPageNumbers(totalPages)}
            ${nextBtn}
          </ul>
        </nav>
      </div>
    `;
  }

  buildPageNumbers(totalPages) {
    const { page } = this.filters;
    const maxVisible = 7;
    let start = Math.max(1, page - 3);
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    let html = '';
    
    // First page + ellipsis
    if (start > 1) {
      html += '<li><a href="#" class="pagination__item link" data-page="1">1</a></li>';
      if (start > 2) {
        html += '<li><span class="pagination__item">...</span></li>';
      }
    }
    
    // Page numbers
    for (let i = start; i <= end; i++) {
      const current = i === page;
      const currentClass = current ? 'pagination__item--current light' : 'link';
      const ariaCurrent = current ? ' aria-current="page"' : '';
      html += `<li><a href="#" class="pagination__item ${currentClass}" data-page="${i}"${ariaCurrent}>${i}</a></li>`;
    }
    
    // Last page + ellipsis
    if (end < totalPages) {
      if (end < totalPages - 1) {
        html += '<li><span class="pagination__item">...</span></li>';
      }
      html += `<li><a href="#" class="pagination__item link" data-page="${totalPages}">${totalPages}</a></li>`;
    }
    
    return html;
  }

  removePagination() {
    const container = document.querySelector('.main-blog__client-pagination');
    if (container) {
      container.style.display = 'none';
      container.innerHTML = '';
    }
  }

  hideServerPagination() {
    const serverPagination = document.querySelector('.pagination-wrapper:not(.main-blog__client-pagination .pagination-wrapper)');
    if (serverPagination) {
      serverPagination.style.display = 'none';
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BlogFilters());
} else {
  new BlogFilters();
}
